import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Check, Menu } from 'lucide-react';
import { BasketIcon } from './BasketIcon';
import HowItWorks from './HowItWorks/HowItWorks';
import { UserContext } from '../UserContext';
import { navigateToResumedWorkspace } from '../lib/resolveMostRecentWorkspace';

const features = [
  {
    image: { src: "/images/personalized-learning.jpeg", alt: "Relational learning" },
    label: "Relational learning",
    headline: "How does Vagus nerve help you remember Multiple Myeloma?",
    description:
      "Vagus nerve is cranial nerve 10. Multiple Myeloma is diagnosed at 10% or more clonal plasma cells. One familiar anchor. One hard fact remembered."
  },
  {
    image: { src: "/images/rotational-learning.jpeg", alt: "Rotational learning" },
    label: "Rotational learning",
    headline: "Old topics come back every time you add a new one.",
    description:
      "When you search for a basket to place your new topic into, you see older topics. That moment of scanning is your review. It happens in the flow of moving forward, not in a separate session."
  },
  {
    image: { src: "/images/reduced-study-volume.jpeg", alt: "Reduced study volume" },
    label: "Reduced study volume",
    headline: "Understand one. Remember the whole group.",
    description:
      "Topics that share a hard detail reinforce each other. Add a new one and the familiar ones sharpen it. The basket grows, but your workload doesn't."
  }
];

const FeatureShowcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToFeature = useCallback((index) => {
    setCurrentIndex((prevIndex) => {
      if (index < 0) return features.length - 1;
      if (index >= features.length) return 0;
      return index;
    });
  }, []);

  const nextFeature = useCallback(() => goToFeature(currentIndex + 1), [currentIndex, goToFeature]);
  const prevFeature = useCallback(() => goToFeature(currentIndex - 1), [currentIndex, goToFeature]);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="relative pb-[56.25%]"> {/* 16:9 aspect ratio */}
        <img
          src={features[currentIndex].image.src}
          alt={features[currentIndex].image.alt}
          className="absolute top-0 left-0 w-full h-full object-cover"
        />
        <button
          onClick={prevFeature}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-cranberry-700/80 rounded-full p-2 hover:bg-cranberry-600 transition-colors shadow-md"
          aria-label="Previous feature"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={nextFeature}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-cranberry-700/80 rounded-full p-2 hover:bg-cranberry-600 transition-colors shadow-md"
          aria-label="Next feature"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>
      <div className="p-6 pt-2"> {/* Changed padding-top to 2 */}
        <div className="flex justify-center mb-3 space-x-2"> {/* Reduced margin-bottom to 3 */}
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => goToFeature(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? "bg-cranberry-700" : "bg-neutral-200 hover:bg-neutral-400"
              }`}
              aria-label={`Go to feature ${index + 1}`}
            />
          ))}
        </div>
        <p className="text-lg font-medium uppercase tracking-wide text-cranberry-700 mb-2">
          {features[currentIndex].label}
        </p>
        <h3 className="text-2xl sm:text-3xl font-bold mb-3 text-neutral-900 leading-tight">
          {features[currentIndex].headline}
        </h3>
        <p className="text-neutral-600 leading-relaxed">{features[currentIndex].description}</p>
      </div>
    </div>
  );
};

const PricingTable = () => (
  <div className="max-w-6xl mx-auto">
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-max">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-4 text-left text-lg font-semibold">Features</th>
              <th className="px-4 py-4 text-center">
                <div className="text-lg font-semibold">Free</div>
              </th>
              <th className="px-4 py-4 text-center">
                <div className="text-lg font-semibold">3 Months</div>
              </th>
              <th className="px-4 py-4 text-center">
                <div className="text-lg font-semibold">6 Months</div>
                <div className="mt-2">
                  <span className="bg-cranberry-100 text-cranberry-700 px-3 py-1 rounded-full text-sm">
                    Recommended
                  </span>
                </div>
              </th>
              <th className="px-4 py-4 text-center">
                <div className="text-lg font-semibold">12 Months</div>
                <div className="mt-2">
                  <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm">
                    Best Value
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-4 py-4 text-gray-600">Price</td>
              <td className="px-4 py-4 text-center">$0</td>
              <td className="px-4 py-4 text-center">
                <div>$16.00</div>
              </td>
              <td className="px-4 py-4 text-center">
                <div>$26.50</div>
                <div className="text-sm text-emerald-600">Save 17%</div>
              </td>
              <td className="px-4 py-4 text-center">
                <div>$42.50</div>
                <div className="text-sm text-emerald-600">Save 33%</div>
              </td>
            </tr>
            <tr>
              <td className="px-4 py-4 text-gray-600">Monthly Cost</td>
              <td className="px-4 py-4 text-center">$0</td>
              <td className="px-4 py-4 text-center">$5.33</td>
              <td className="px-4 py-4 text-center">$4.42</td>
              <td className="px-4 py-4 text-center">$3.54</td>
            </tr>
            <tr>
              <td className="px-4 py-4 text-gray-600">Topics per Basket</td>
              <td className="px-4 py-4 text-center">Unlimited</td>
              <td className="px-4 py-4 text-center">Unlimited</td>
              <td className="px-4 py-4 text-center">Unlimited</td>
              <td className="px-4 py-4 text-center">Unlimited</td>
            </tr>
            <tr>
              <td className="px-4 py-4 text-gray-600">Mastery Filters</td>
              <td className="px-4 py-4 text-center">
                <X className="h-5 w-5 text-gray-400 mx-auto" />
              </td>
              <td className="px-4 py-4 text-center">
                <Check className="h-5 w-5 text-emerald-500 mx-auto" />
              </td>
              <td className="px-4 py-4 text-center">
                <Check className="h-5 w-5 text-emerald-500 mx-auto" />
              </td>
              <td className="px-4 py-4 text-center">
                <Check className="h-5 w-5 text-emerald-500 mx-auto" />
              </td>
            </tr>
            <tr>
              <td className="px-4 py-4 text-gray-600">Progress Insights</td>
              <td className="px-4 py-4 text-center">
                <X className="h-5 w-5 text-gray-400 mx-auto" />
              </td>
              <td className="px-4 py-4 text-center">
                <Check className="h-5 w-5 text-emerald-500 mx-auto" />
              </td>
              <td className="px-4 py-4 text-center">
                <Check className="h-5 w-5 text-emerald-500 mx-auto" />
              </td>
              <td className="px-4 py-4 text-center">
                <Check className="h-5 w-5 text-emerald-500 mx-auto" />
              </td>
            </tr>
            <tr>
              <td className="px-4 py-4 text-gray-600">Early Access Features</td>
              <td className="px-4 py-4 text-center">
                <X className="h-5 w-5 text-gray-400 mx-auto" />
              </td>
              <td className="px-4 py-4 text-center">
                <Check className="h-5 w-5 text-emerald-500 mx-auto" />
              </td>
              <td className="px-4 py-4 text-center">
                <Check className="h-5 w-5 text-emerald-500 mx-auto" />
              </td>
              <td className="px-4 py-4 text-center">
                <Check className="h-5 w-5 text-emerald-500 mx-auto" />
              </td>
            </tr>
            <tr>
              <td className="px-4 py-4 text-gray-600">Description</td>
              <td className="px-4 py-4 text-center text-sm text-gray-500">
                Get a taste of our unique learning approach
              </td>
              <td className="px-4 py-4 text-center text-sm text-gray-500">
                Perfect for short-term study goals with less than 5 subjects
              </td>
              <td className="px-4 py-4 text-center text-sm text-gray-500">
                Best for most students with 5+ subjects
              </td>
              <td className="px-4 py-4 text-center text-sm text-gray-500">
                Ideal if you want to take your time and save the most
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="bg-gray-50 px-4 py-4">
        <div className="text-center text-sm text-gray-600 max-w-2xl mx-auto">
          <span className="font-medium text-gray-900">Why choose the 6-month plan? </span>
          Best for most students with 5+ subjects. Save 17% compared to the 3-month plan while having enough time to build strong connections between topics.
        </div>
      </div>
    </div>
  </div>
);

const ComparisonTable = () => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-[#FDF2F3]">
          <th className="border-2 border-[#0F172A] p-4 text-[#0F172A]">Features</th>
          <th className="border-2 border-[#0F172A] p-4 text-[#0F172A]">Basket</th>
          <th className="border-2 border-[#0F172A] p-4 text-[#0F172A]">Traditional Flashcards</th>
          <th className="border-2 border-[#0F172A] p-4 text-[#0F172A]">Note-Taking Apps</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="border-2 border-[#0F172A] p-4 text-[#475569]">Rotational Learning</td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
        </tr>
        <tr>
          <td className="border-2 border-[#0F172A] p-4 text-[#475569]">Relational Learning</td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
        </tr>
        <tr>
          <td className="border-2 border-[#0F172A] p-4 text-[#475569]">Reduced Study Volume</td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><X className="text-[#DC2626] mx-auto" /></td>
        </tr>
        <tr>
          <td className="border-2 border-[#0F172A] p-4 text-[#475569]">Active Recall</td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
          <td className="border-2 border-[#0F172A] p-4"><Check className="text-[#059669] mx-auto" /></td>
        </tr>
      </tbody>
    </table>
  </div>
);

const PrimaryButton = ({ onClick, children, className = '' }) => (
  <button 
    onClick={onClick}
    className={`bg-butter-500 text-neutral-900 px-6 py-2 rounded-xl border-2 border-neutral-900 font-medium hover:bg-butter-600 transition-colors duration-300 text-lg shadow-md ${className}`}
  >
    {children}
  </button>
);

const SecondaryButton = ({ onClick, children, className = '' }) => (
  <button 
    onClick={onClick}
    className={`bg-white text-neutral-900 px-6 py-2 rounded-xl border-2 border-neutral-900 font-medium hover:bg-neutral-50 transition-colors duration-300 text-lg shadow-md ${className}`}
  >
    {children}
  </button>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const { user } = useContext(UserContext);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (user) {
          await navigateToResumedWorkspace(navigate, user.id, { replace: true });
        }
      } catch (error) {
        console.error('Error resolving workspace home:', error);
        navigate('/workspace', { replace: true });
      } finally {
        setIsChecking(false);
      }
    };
    checkAuth();
  }, [user, navigate]);

  const handleGetStarted = () => {
    navigate('/auth', { state: { isSignup: true, email: signupEmail } });
  };

  const handleLogin = () => {
    navigate('/auth', { state: { isLogin: true } });
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    document.getElementById('top')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (isChecking) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cranberry-700"></div>
      </div>
    );
  }

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50 font-['Inter', sans-serif]">
      {/* Navbar */}
      <nav className="bg-cranberry-700 shadow-md fixed top-0 left-0 right-0 z-10 border-b-2 border-neutral-900">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
          <a href="#top" onClick={handleLogoClick} className="flex items-center">
            <BasketIcon color="#FFFFFF" className="w-6 h-6 mr-2" />
            <span className="text-xl font-bold text-white">Basket</span>
          </a>
          
          {/* Mobile menu button */}
          <button className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <Menu className="text-white w-6 h-6" />
          </button>

          {/* Desktop menu */}
          <div className="hidden md:flex space-x-2 sm:space-x-4 items-center">
            <a href="#how-it-works" className="text-white hover:text-butter-200 transition-colors duration-200 text-sm">Workflow</a>
            <a href="#pricing" className="text-white hover:text-butter-200 transition-colors duration-200 text-sm">Pricing</a>
            <SecondaryButton onClick={handleLogin} className="text-sm py-1 px-3 sm:px-4">
              Log In
            </SecondaryButton>
            <PrimaryButton onClick={handleGetStarted} className="text-sm py-1 px-3 sm:px-4">
              Get Started
            </PrimaryButton>
          </div>

          {/* Mobile menu panel */}
          {isMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-cranberry-700 md:hidden p-4 border-b-2 border-neutral-900">
              <div className="flex flex-col space-y-4">
                <a href="#how-it-works" className="text-white hover:text-butter-200 transition-colors duration-200">Workflow</a>
                <a href="#pricing" className="text-white hover:text-butter-200 transition-colors duration-200">Pricing</a>
                <SecondaryButton onClick={handleLogin} className="w-full">Log In</SecondaryButton>
                <PrimaryButton onClick={handleGetStarted} className="w-full">Get Started</PrimaryButton>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20">
        {/* Hero Section */}
        <section
          id="top"
          className="flex flex-col lg:flex-row items-center mb-16 scroll-mt-20 sm:scroll-mt-24 bg-white rounded-xl shadow-md overflow-hidden"
        >
          <div className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4 leading-tight">
              <span className="block">Use what you remember</span>
              <span className="block">to recall what you forget.</span>
            </h1>
            <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
              Connect the ideas you're studying and practise recalling the links between them, so one remembered idea can help you retrieve another.
            </p>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  className="flex-grow px-4 py-3 rounded-xl border-2 border-neutral-900 focus:border-cranberry-700 focus:ring-1 focus:ring-cranberry-700 outline-none text-lg"
                  aria-label="Email for signup"
                />
                <PrimaryButton onClick={handleGetStarted} className="w-full sm:w-auto text-xl py-3 whitespace-nowrap">
                  Get Started
                </PrimaryButton>
              </div>
              <p className="text-center text-sm text-neutral-600">No credit card required</p>
            </div>
          </div>
          <div className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8">
            <FeatureShowcase />
          </div>
        </section>

        {/* Problem Section */}
        <section
          id="the-problem"
          className="mb-8 sm:mb-12 lg:mb-16 bg-white rounded-xl border-2 border-[#0F172A] p-4 sm:p-6 lg:p-8 shadow-md"
          aria-labelledby="the-problem-heading"
        >
          <header className="mb-8 text-center">
            <p className="text-base font-bold uppercase tracking-widest text-cranberry-800 mb-4">
              The problem
            </p>
            <h2 id="the-problem-heading" className="text-3xl font-bold text-[#0F172A] mb-4">
              The problem isn&apos;t understanding. It&apos;s volume.
            </h2>
            <p className="text-lg text-[#475569] max-w-2xl mx-auto">
              As topics pile up across subjects, recall doesn&apos;t keep pace.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <figure className="bg-[#FDF2F3] rounded-xl border-2 border-[#0F172A] overflow-hidden shadow-md">
              <figcaption className="px-4 py-2.5 text-center border-b-2 border-[#0F172A]">
                <span className="inline-block px-3 py-0.5 rounded-full text-xs font-semibold bg-neutral-100 text-neutral-700 border border-neutral-300">
                  Without Basket
                </span>
                <p className="mt-1.5 text-sm text-[#475569] leading-snug">Recall drops as topics grow</p>
              </figcaption>
              <img
                src="/images/problem-without-basket.png"
                alt="Chart showing recall declining as study topics increase over time without Basket"
                className="w-full h-auto object-contain"
              />
            </figure>

            <figure className="bg-[#FDF2F3] rounded-xl border-4 border-cranberry-700 overflow-hidden shadow-lg ring-2 ring-cranberry-200">
              <figcaption className="px-4 py-2.5 text-center border-b-2 border-cranberry-700">
                <span className="inline-block px-3 py-0.5 rounded-full text-xs font-semibold bg-cranberry-100 text-cranberry-800 border border-cranberry-300">
                  With Basket
                </span>
                <p className="mt-1.5 text-sm text-[#475569] leading-snug">Recall stays high throughout</p>
              </figcaption>
              <img
                src="/images/problem-with-basket.png"
                alt="Chart showing recall staying high as study topics increase over time with Basket"
                className="w-full h-auto object-contain"
              />
            </figure>
          </div>
        </section>

        <HowItWorks
          images={[
            '/images/hiw-step-1-mark-detail.png',
            '/images/hiw-step-2-find-basket.png',
            '/images/hiw-step-3-next-topic.png',
            '/images/hiw-step-4-qbank-mastery.png',
          ]}
        />

        {/* Features Section */}
        <section id="features" className="mb-8 sm:mb-12 lg:mb-16 bg-white rounded-xl border-2 border-[#0F172A] p-4 sm:p-6 lg:p-8 shadow-md">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#0F172A]">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#FDF2F3] p-6 rounded-xl border-2 border-[#0F172A] shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-[#0F172A]">Rotational Learning</h3>
              <p className="text-[#475569]">
                Review old topics naturally while learning new ones. No separate review sessions needed.
              </p>
            </div>
            
            <div className="bg-[#FDF2F3] p-6 rounded-xl border-2 border-[#0F172A] shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-[#0F172A]">Relational Learning</h3>
              <p className="text-[#475569]">
                Connect new concepts to familiar ones, making difficult topics easier to understand and remember.
              </p>
            </div>
            
            <div className="bg-[#FDF2F3] p-6 rounded-xl border-2 border-[#0F172A] shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-[#0F172A]">Reduced Study Volume</h3>
              <p className="text-[#475569]">
                Learn more with less material by focusing on connections between topics rather than memorizing each separately.
              </p>
            </div>
          </div>
        </section>

        {/* Comparison Section - Moved down */}
        <section className="mb-8 sm:mb-12 lg:mb-16 bg-white rounded-xl border-2 border-[#0F172A] p-4 sm:p-6 lg:p-8 shadow-md">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#0F172A]">How Basket Compares to Other Study Tools</h2>
          <ComparisonTable />
          <div className="mt-8 text-center">
            <p className="text-lg text-[#475569] mb-4 max-w-2xl mx-auto">
              Basket combines the best features of traditional study methods with innovative learning techniques, 
              providing a unique and effective study experience that adapts to your needs.
            </p>
            <PrimaryButton onClick={handleGetStarted}>
              Experience the Basket Difference
            </PrimaryButton>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="mb-8 sm:mb-12 lg:mb-16 scroll-mt-20 sm:scroll-mt-24 bg-white rounded-xl border-2 border-[#0F172A] p-4 sm:p-6 lg:p-8 shadow-md">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#0F172A]">Simple, Transparent Pricing</h2>
          <PricingTable />
          <div className="text-center mt-8">
            <PrimaryButton onClick={handleGetStarted} className="text-xl">
              Try Free Plan
            </PrimaryButton>
            <p className="mt-4 text-sm text-[#475569]">No credit card required for free plan</p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center mb-8 sm:mb-12 lg:mb-16 bg-[#741102] py-4 sm:py-6 lg:py-8 px-4 sm:px-6 lg:px-8 rounded-xl border-2 border-[#0F172A] shadow-md">
          <h2 className="text-4xl font-bold mb-4 text-white">Try it! You have nothing to lose but a lot to gain.</h2>
          <p className="text-xl text-[#F5CCD0] mb-6 max-w-3xl mx-auto">Join thousands of students who conquered their exams with Basket</p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3 max-w-xl mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              value={signupEmail}
              onChange={(e) => setSignupEmail(e.target.value)}
              className="flex-grow px-4 py-3 rounded-xl border-2 border-neutral-900 focus:border-cranberry-700 focus:ring-1 focus:ring-cranberry-700 outline-none text-lg bg-white"
              aria-label="Email for signup"
            />
            <PrimaryButton onClick={handleGetStarted} className="text-xl px-8 py-3 whitespace-nowrap">
              Start Learning Now
            </PrimaryButton>
          </div>
          
          <p className="mt-4 text-sm text-[#F5CCD0]">Free plan available</p>
        </section>

        {/* Footer */}
        <footer className="text-center text-sm text-neutral-600 border-t-2 border-neutral-200 pt-6 pb-4">
          <p>&copy; 2024 Basket Learning. All rights reserved.</p>
          <div className="mt-4 space-x-6">
            <button type="button" className="hover:text-cranberry-700 transition-colors duration-200 bg-transparent border-none cursor-pointer">About Us</button>
            <button type="button" className="hover:text-cranberry-700 transition-colors duration-200 bg-transparent border-none cursor-pointer">FAQ</button>
            <Link to="/terms-of-service" className="hover:text-cranberry-700 transition-colors duration-200">Terms of Service</Link>
            <Link to="/privacy-policy" className="hover:text-cranberry-700 transition-colors duration-200">Privacy Policy</Link>
          </div>
        </footer>
      </div>

      {showDemo && (
        <div className="fixed inset-0 bg-[#0F172A] bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl border-2 border-[#0F172A] shadow-2xl relative max-w-4xl w-full">
            <button 
              onClick={() => setShowDemo(false)} 
              className="absolute top-4 right-4 text-[#475569] hover:text-[#0F172A] transition-colors duration-200"
            >
              <X size={32} />
            </button>
            <iframe 
              width="100%" 
              height="480" 
              src="https://www.youtube.com/embed/dQw4w9WgXcQ" 
              title="Basket Demo Video" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
              className="rounded-xl border-2 border-[#0F172A]"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
